/**
 * Система пресетов для визуализации эффектов
 * Stage 4: Улучшение фронтенда для эффектов
 */

import { STATUS_EFFECTS } from './statusEffects.js';

/**
 * Категории эффектов для группировки в UI
 */
export const EFFECT_CATEGORIES = {
  BUFF: 'buff',
  DEBUFF: 'debuff',
  DOT: 'dot',
  HOT: 'hot',
  SHIELD: 'shield',
  CONTROL: 'control',
  POSITION: 'position',
  TRIGGER: 'trigger',
  MASS: 'mass',
  OTHER: 'other'
};

/**
 * Цвета для категорий эффектов
 */
export const EFFECT_CATEGORY_COLORS = {
  [EFFECT_CATEGORIES.BUFF]: '#10b981',      // Зелёный
  [EFFECT_CATEGORIES.DEBUFF]: '#ef4444',    // Красный
  [EFFECT_CATEGORIES.DOT]: '#f59e0b',       // Оранжевый
  [EFFECT_CATEGORIES.HOT]: '#3b82f6',      // Синий
  [EFFECT_CATEGORIES.SHIELD]: '#8b5cf6',   // Фиолетовый
  [EFFECT_CATEGORIES.CONTROL]: '#ec4899',  // Розовый
  [EFFECT_CATEGORIES.POSITION]: '#06b6d4', // Голубой
  [EFFECT_CATEGORIES.TRIGGER]: '#f97316',  // Оранжевый
  [EFFECT_CATEGORIES.MASS]: '#6366f1',      // Индиго
  [EFFECT_CATEGORIES.OTHER]: '#6b7280'      // Серый
};

/**
 * Определяет категорию эффекта по его типу
 */
export function getEffectCategory(statusType) {
  if (!statusType) return EFFECT_CATEGORIES.OTHER;
  
  const type = String(statusType).toLowerCase();
  
  // Дебаффы (проверяем первыми, чтобы иметь приоритет над DoT)
  if (type.includes('debuff') || type.includes('penalty') || type.includes('drain') ||
      type.includes('vulnerable') || type.includes('limit') || type.includes('curse')) {
    return EFFECT_CATEGORIES.DEBUFF;
  }
  
  // Контроль (станы, молчание, пропуски ходов) - проверяем перед позицией
  if (type.includes('freeze') || type.includes('skip') || type.includes('silence') ||
      type.includes('stun') || type.includes('block')) {
    return EFFECT_CATEGORIES.CONTROL;
  }
  
  // DoT эффекты
  if (type.includes('dot') || type.includes('burn') || type.includes('poison') || 
      type.includes('thermal') || type.includes('plague') || type.includes('decay')) {
    return EFFECT_CATEGORIES.DOT;
  }
  
  // HoT эффекты
  if (type.includes('hot') || type.includes('regen') || type.includes('heal')) {
    return EFFECT_CATEGORIES.HOT;
  }
  
  // Щиты
  if (type.includes('shield') || type.includes('barrier') || type.includes('armor') ||
      type.includes('guard') || type.includes('phoenix')) {
    return EFFECT_CATEGORIES.SHIELD;
  }
  
  // Контроль позиции
  if (type.includes('swap') || type.includes('move') || type.includes('position') ||
      type.includes('root') || type.includes('bind') || type.includes('lock')) {
    return EFFECT_CATEGORIES.POSITION;
  }
  
  // Баффы
  if (type.includes('buff') || type.includes('boost') || type.includes('haste') ||
      type.includes('speed') || type.includes('attack') || type.includes('crit') ||
      type.includes('evade') || type.includes('bonus')) {
    return EFFECT_CATEGORIES.BUFF;
  }
  
  // Массовые эффекты
  if (type.includes('mass') || type.includes('all') || type.includes('cathedral') ||
      type.includes('storm') || type.includes('flood')) {
    return EFFECT_CATEGORIES.MASS;
  }
  
  // Триггеры
  if (type.includes('trigger') || type.includes('on') || type.includes('when') ||
      type.includes('propagation') || type.includes('spread')) {
    return EFFECT_CATEGORIES.TRIGGER;
  }
  
  return EFFECT_CATEGORIES.OTHER;
}

/**
 * Получает цвет для эффекта
 */
export function getEffectColor(statusType, defaultColor = null) {
  const category = getEffectCategory(statusType);
  return EFFECT_CATEGORY_COLORS[category] || defaultColor || EFFECT_CATEGORY_COLORS[EFFECT_CATEGORIES.OTHER];
}

/**
 * Расширенный пресет эффекта с дополнительной информацией для UI
 */
export function enhanceStatusPreset(status, statusType = null) {
  if (!status) return null;
  
  const type = statusType || status.type;
  const category = getEffectCategory(type);
  const color = getEffectColor(type);
  
  // Определяем, является ли эффект временным (имеет срок действия)
  const isTemporary = status.expiresAt != null;
  
  // Определяем, является ли эффект стековым
  const isStackable = status.stackCount != null && status.maxStacks != null && status.maxStacks > 1;
  
  return {
    ...status,
    category,
    color,
    isTemporary,
    isStackable,
    stackCount: status.stackCount || null,
    maxStacks: status.maxStacks || null
  };
}

/**
 * Группирует статусы по категориям для отображения
 */
export function groupStatusesByCategory(statuses) {
  const groups = {};
  
  for (const status of statuses) {
    const enhanced = enhanceStatusPreset(status);
    if (!enhanced) continue;
    
    const category = enhanced.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(enhanced);
  }
  
  return groups;
}

/**
 * Форматирует отображение статуса с учётом категории и стека
 */
export function formatStatusDisplay(status, options = {}) {
  const {
    showIcon = true,
    showLabel = true,
    showStack = true,
    showDuration = false,
    currentRound = null
  } = options;
  
  const enhanced = enhanceStatusPreset(status);
  if (!enhanced) return '';
  
  const parts = [];
  
  // Иконка
  if (showIcon && enhanced.icon) {
    parts.push(enhanced.icon);
  }
  
  // Лейбл
  if (showLabel) {
    let label = enhanced.shortLabel || enhanced.label || '';
    
    // Добавляем стек, если есть
    if (showStack && enhanced.isStackable && enhanced.stackCount) {
      label += ` ×${enhanced.stackCount}`;
      if (enhanced.maxStacks) {
        label += `/${enhanced.maxStacks}`;
      }
    }
    
    if (label) {
      parts.push(label);
    }
  }
  
  // Длительность
  if (showDuration && enhanced.isTemporary && enhanced.expiresAt != null && currentRound != null) {
    const remaining = enhanced.expiresAt - currentRound;
    if (remaining > 0) {
      parts.push(`(${remaining})`);
    }
  }
  
  return parts.join(' ');
}

/**
 * Создаёт HTML для бейджа статуса с улучшенной визуализацией
 */
export function createStatusBadgeHtml(status, options = {}) {
  const {
    showTooltip = true,
    showStack = true,
    showDuration = false,
    currentRound = null,
    className = 'battle-status-badge'
  } = options;
  
  const enhanced = enhanceStatusPreset(status);
  if (!enhanced) return '';
  
  const display = formatStatusDisplay(status, { showStack, showDuration, currentRound });
  const tooltip = showTooltip ? (status.description || status.label || status.shortLabel || '') : '';
  
  const style = enhanced.color ? `border-left: 3px solid ${enhanced.color};` : '';
  const categoryClass = `battle-status-badge--${enhanced.category}`;
  
  return `<span class="${className} ${categoryClass}" style="${style}" title="${tooltip || ''}">${display}</span>`;
}

/**
 * Создаёт HTML для группы статусов одной категории
 */
export function createStatusGroupHtml(category, statuses, options = {}) {
  if (!statuses || statuses.length === 0) return '';
  
  const enhanced = statuses.map(s => enhanceStatusPreset(s)).filter(Boolean);
  const color = EFFECT_CATEGORY_COLORS[category] || EFFECT_CATEGORY_COLORS[EFFECT_CATEGORIES.OTHER];
  
  const badges = enhanced.map(status => createStatusBadgeHtml(status, options)).join('');
  
  return `<div class="battle-status-group battle-status-group--${category}" style="border-left: 2px solid ${color};">
    ${badges}
  </div>`;
}

/**
 * Создаёт HTML для всех статусов с группировкой по категориям
 */
export function createGroupedStatusBadgesHtml(statuses, options = {}) {
  if (!statuses || statuses.length === 0) return '';
  
  const groups = groupStatusesByCategory(statuses);
  
  // Порядок отображения категорий
  const categoryOrder = [
    EFFECT_CATEGORIES.SHIELD,
    EFFECT_CATEGORIES.BUFF,
    EFFECT_CATEGORIES.HOT,
    EFFECT_CATEGORIES.DEBUFF,
    EFFECT_CATEGORIES.DOT,
    EFFECT_CATEGORIES.CONTROL,
    EFFECT_CATEGORIES.POSITION,
    EFFECT_CATEGORIES.TRIGGER,
    EFFECT_CATEGORIES.MASS,
    EFFECT_CATEGORIES.OTHER
  ];
  
  const html = categoryOrder
    .filter(cat => groups[cat] && groups[cat].length > 0)
    .map(cat => createStatusGroupHtml(cat, groups[cat], options))
    .join('');
  
  return html || '';
}

/**
 * Проверяет, является ли эффект критическим (требует визуального акцента)
 */
export function isCriticalEffect(statusType) {
  const type = String(statusType || '').toLowerCase();
  
  // Критические эффекты: щиты, станы, массовые эффекты
  return type.includes('shield') || type.includes('freeze') || type.includes('stun') ||
         type.includes('cathedral') || type.includes('storm') || type.includes('flood') ||
         type.includes('phoenix') || type.includes('resurrect');
}

/**
 * Получает анимацию для эффекта
 */
export function getEffectAnimation(statusType, eventType = 'apply') {
  const type = String(statusType || '').toLowerCase();
  const category = getEffectCategory(statusType);
  
  // Анимации по категориям
  const animations = {
    [EFFECT_CATEGORIES.SHIELD]: {
      apply: 'pulse',
      tick: 'glow',
      remove: 'fade-out'
    },
    [EFFECT_CATEGORIES.DOT]: {
      apply: 'shake',
      tick: 'flash-red',
      remove: 'fade-out'
    },
    [EFFECT_CATEGORIES.HOT]: {
      apply: 'glow-green',
      tick: 'pulse-green',
      remove: 'fade-out'
    },
    [EFFECT_CATEGORIES.CONTROL]: {
      apply: 'freeze',
      tick: 'glow',
      remove: 'fade-out'
    }
  };
  
  return animations[category]?.[eventType] || null;
}


